import React, { useRef, useState } from 'react'
import ReactModal from 'react-modal'
import '../../../styles/modals/PollScheduleModal.css'
import { FaTimes, FaPause, FaTrash, FaPlay } from 'react-icons/fa'
import { getRunningPrograms } from '../../../server/server'
import { useUser } from '../../../auth'
import ReactTooltip from 'react-tooltip'
import { changeProgramStatus, removeRunningProgram } from '../../../server/server'
import ConfirmationModal from '../../modals/ConfirmationModal'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { ReactComponent as RunningIcon } from '../../../assets/svg/RunningIcon.svg'
import { ReactComponent as DeleteContent } from '../../../assets/svg/DeleteContent.svg'
import { ReactComponent as NotFoundIcon } from '../../../assets/svg/NotFoundIcon.svg'

import { ReactComponent as StopRun } from '../../../assets/svg/StopRun.svg'
import { ReactComponent as StartRun } from '../../../assets/svg/StartRun.svg'
import { notify, translateTargetText } from '../../../utils/util'
import TargetPreviewModal from '../../modals/TargetPreviewModal'
import { ANALYTICS_EVENTS } from '../../../analytics/events'
import useTrackedSurface from '../../../analytics/useTrackedSurface'

function RunningProgram({ selectedRunningProgram, onProgramsChanged }) {
    const { user, setUser } = useUser()
    const { t, locale } = useTranslation()
    const [elements, setElements] = useState([])
    const [loading, setLoading] = useState(true)
    const requestRef = useRef(0)
    const [targetModalOpen, setTargetModalOpen] = useState(false)
    const [selectedProgram, setSelectedProgram] = useState(null)
    const [deleteModalOpen, setDelModalOpen] = useState({
        isOpen: false,
        type: '',
    })
    const [selProgram, setSelProgram] = useState({})
    useTrackedSurface(ANALYTICS_EVENTS.CONTENT_LIBRARY_RUNNING_PROGRAM_VIEWED, {
        contentId: selectedRunningProgram?.length === 1 ? selectedRunningProgram[0] : 'all-running-programs',
        content: selectedRunningProgram?.length === 1 ? 'Running program' : 'Running programs',
    })
    const closeDelModal = () => {
        setDelModalOpen({
            isOpen: false,
            type: '',
        })
    }
    const getPrograms = async () => {
        const requestId = ++requestRef.current
        setLoading(true)
        try {
            const response = await getRunningPrograms({ user })
            if (requestId !== requestRef.current) return
            if (response.status === 200) {
                const list = Array.isArray(response.data) ? response.data : []
                setElements(selectedRunningProgram.length !== 0 ? list.filter((item) => selectedRunningProgram.includes(item.id)) : list)
            } else {
                setElements([])
            }
        } catch (err) {
            if (requestId !== requestRef.current) return
            console.error('RunningProgram: failed to load running programs', err)
            setElements([])
        } finally {
            if (requestId === requestRef.current) setLoading(false)
        }
    }

    React.useEffect(() => {
        getPrograms()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRunningProgram])
    const handleChange = async () => {
        closeDelModal()
        const nextProgram = { ...selProgram, status: selProgram.status === 'start' ? 'stop' : 'start' }
        const response = await changeProgramStatus({
            program: nextProgram,
        })
        if (response.status === 200) {
            setElements((prev) => prev.map((item) => (item.id === nextProgram.id ? nextProgram : item)))
            setSelProgram(nextProgram)
        }
    }
    const changeStatus = (program, type) => {
        setSelProgram(program)
        setDelModalOpen({ isOpen: true, type: type })
    }
    const handleDelete = async () => {
        closeDelModal()
        const response = await removeRunningProgram({
            program: selProgram,
        })
        if (response.status === 200) {
            setElements((prev) => prev.filter((item) => item.id !== selProgram.id))
            notify(t('Program has been deleted!'))
            onProgramsChanged?.()
        }
    }
    const switchConfirm = () => {
        if (deleteModalOpen.type === 'del') {
            handleDelete()
        } else {
            handleChange()
        }
    }

    const closeTargetModal = () => {
        setTargetModalOpen(false)
    }

    const openTarget = (program) => {
        setSelectedProgram(program)
        setTargetModalOpen(true)
    }

    return (
        <div className="bg-white w-full max-w-[1200px] min-h-[500px] rounded-xl flex flex-col h-max">
            <div className="px-14 p-8 rounded-xl w-full">
                <div className="flex justify-start items-center">
                    <RunningIcon />
                    <span className="text-deepPurple font-bold text-lg max-w-[550px] text-left ml-5">{t('This tool lets you manage your active programs')}</span>
                </div>
                <div className=" mt-5" style={{ overflowY: 'auto' }}>
                    {loading && <RunningProgramSkeleton rows={selectedRunningProgram.length || 3} />}
                    {!loading &&
                        elements?.map((element, index) => {
                            return (
                                <div
                                    className="rtg-running-row running-program-module flex items-center mb-3 overflow-hidden rounded-xl border border-containerGray hover:border-deepPurple pointer"
                                    key={element.id ?? index}
                                >
                                    <div id={`running-program-${element.id}`} className="bg-containerGray rounded-xl w-full min-w-0 min-h-[80px] py-3 flex flex-wrap justify-between items-center gap-x-4 gap-y-2 px-4">
                                        <p className="text-deepPurple text-xl font-bold text-left min-w-0 truncate" title={element.program.name}>
                                            {element.program.name}
                                        </p>
                                        <div className="flex flex-wrap items-center gap-y-2">
                                            <div className="flex gap-2 bg-lightBG rounded-full px-4 h-[40px] items-center text-base font-semibold text-deselectPurple mr-3">
                                                {element.targetDays.map((day, index) => {
                                                    return (
                                                        <div key={index} className={day.selected ? 'text-deepPurple bg-white rounded-full p-1 px-2' : 'p-1 px-2'}>
                                                            {t(day.name)}
                                                        </div>
                                                    )
                                                })}
                                            </div>

                                            <p className="h-[40px] px-3 flex items-center mr-5 text-deepPurple font-semibold bg-lightBG rounded-full">
                                                {element.status == 'start' ? t('running') : t('paused')}
                                            </p>
                                            <button
                                                className="h-[40px] px-3 flex items-center text-deepPurple font-semibold bg-lightBG rounded-full"
                                                onClick={() => openTarget(element)}
                                                title="Open to see target preview"
                                            >
                                                {translateTargetText(element.target_text)}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="running-period-module-buttons">
                                        <button
                                            type="button"
                                            className="running-program-status-button"
                                            aria-label={element.status == 'start' ? t('pauseProgram') : t('Resume program')}
                                            data-tip={`${element.status == 'start' ? t('pauseProgram') : t('Resume program')}`}
                                            data-for="PlayPausetip"
                                            onClick={() => changeStatus(element, 'cha')}
                                            style={i18n.language == 'ar' ? { marginRight: '10px' } : {}}
                                        >
                                            {element.status == 'start' ? (
                                                <StopRun className="fill-mudPurple hover:fill-deepPurple" />
                                            ) : (
                                                <StartRun className="fill-mudPurple hover:fill-deepPurple" />
                                            )}
                                            <ReactTooltip globalEventOff="click" id="PlayPausetip" arrowColor="transparent" effect="solid" backgroundColor="rgb(97,97,97)" />
                                        </button>
                                        <button
                                            type="button"
                                            className="running-program-status-button"
                                            aria-label={t('stopProgram')}
                                            style={{ paddingLeft: '10px' }}
                                            data-tip={t('stopProgram')}
                                            data-for="deleteProgram"
                                            onClick={() => changeStatus(element, 'del')}
                                            id={`delete-program-button-${element.id}`}
                                        >
                                            <DeleteContent fill="#FF747F" className="fill-deleteRed hover:fill-[#FF6B3D]" />
                                            <ReactTooltip globalEventOff="click" id="deleteProgram" arrowColor="transparent" effect="solid" backgroundColor="rgb(97,97,97)" />
                                        </button>
                                    </div>
                                </div>
                            )
                        })}
                </div>
            </div>
            {!loading && elements.length === 0 && (
                <div className="rtg-empty flex w-full h-full flex-col items-center justify-center text-deepPurple text-sm">
                    <NotFoundIcon />
                    <div className="mt-3">{t('yourRunningProgramListIsEmptyStartByAddingOneFromTheListOfPrograms')}</div>
                </div>
            )}
            <ConfirmationModal
                modalIsOpen={deleteModalOpen.isOpen}
                closeModal={closeDelModal}
                confirmation={switchConfirm}
                message={
                    deleteModalOpen.type === 'del'
                        ? t('doYouWantToDeleteThisProgram')
                        : selProgram.status === 'start'
                          ? t('Do you want to pause the program?')
                          : t('doYouWantToStartThisProgram')
                }
            />
            <TargetPreviewModal modalIsOpen={targetModalOpen} closeModal={closeTargetModal} program={selectedProgram} />
        </div>
    )
}

/** Placeholder rows matching the 80px program rows while the list loads. */
const RunningProgramSkeleton = ({ rows = 3 }) => (
    <div className="rtg-skeleton" aria-hidden="true">
        {Array.from({ length: rows }).map((_, index) => (
            <div className="flex items-center mb-3 overflow-hidden rounded-xl border border-containerGray" key={index}>
                <div className="bg-containerGray rounded-xl w-full h-[80px] flex justify-between items-center px-4">
                    <div className="rtg-skeleton-block h-[20px] w-[260px]" />
                    <div className="flex items-center gap-3">
                        <div className="rtg-skeleton-block h-[40px] w-[260px] rounded-full" />
                        <div className="rtg-skeleton-block h-[40px] w-[90px] rounded-full" />
                        <div className="rtg-skeleton-block h-[40px] w-[120px] rounded-full" />
                    </div>
                </div>
            </div>
        ))}
    </div>
)

export default RunningProgram
